// client/src/pages/Game/Matchmaking.jsx
// R34f5 — refonte complète :
//  1. Joueur vs Joueur (Classique ou Classé + mise) → file de matchmaking
//  2. Contre l'IA
//  3. Création de partie (code + salle d'attente, on ne démarre que quand
//     l'adversaire a rejoint)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetTutorial } from '../../services/tutorialService';
import { useGame } from '../../hooks/useGame';
import { getUserDecks } from '../../api/deckService';
import { findGames, joinRankedQueue, leaveRankedQueue, getQueueStatus, confirmMatch, declineMatch } from '../../api/gameService';
import socketService from '../../api/socketService';
import './Matchmaking.css';

// R34dd : deck mémorisé entre les parties (localStorage)
const preferredDeckId = () => {
  try { return localStorage.getItem('elementum_preferred_deck'); } catch (e) { return null; }
};

const STAKES = [0, 100, 200, 500];

const Matchmaking = () => {
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [availableGames, setAvailableGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [isJoiningGame, setIsJoiningGame] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('pvp'); // 'pvp' | 'ai' | 'custom'
  const [searchParams] = useSearchParams();
  const tutorialMode = searchParams.get('tutorial') === '1';
  const tutorialStartedRef = React.useRef(false);
  const [aiDifficulty, setAiDifficulty] = useState('medium');

  const navigate = useNavigate();
  const { createNewGame, joinExistingGame } = useGame();

  // PvP : classique / classé + mise
  const [pvpMode, setPvpMode] = useState('casual'); // 'casual' | 'ranked'
  const [stake, setStake] = useState(0);
  const [rankedSearching, setRankedSearching] = useState(false);
  const [queueInfo, setQueueInfo] = useState(null);
  const queuePollRef = React.useRef(null);

  // Création de partie : salle d'attente
  const [lobby, setLobby] = useState(null); // { gameId }

  // Charger decks + parties ouvertes
  const refreshGames = useCallback(async () => {
    try {
      const r = await findGames();
      setAvailableGames((r.games || []).filter((g) => g.id !== (lobby && lobby.gameId)));
    } catch (e) { /* tolérant */ }
  }, [lobby]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const decksResponse = await getUserDecks();
        setDecks(decksResponse.decks);
        // R34dd : deck mémorisé (même choix que le battlefield IA)
        let savedDeck = null;
        try { savedDeck = localStorage.getItem('elementum_preferred_deck'); } catch (e) { /* ok */ }
        if (savedDeck && decksResponse.decks.some((d) => d.id === savedDeck)) {
          setSelectedDeck(savedDeck);
        } else if (decksResponse.decks.length > 0) {
          setSelectedDeck(decksResponse.decks[0].id);
        }
        const gamesResponse = await findGames();
        setAvailableGames(gamesResponse.games || []);
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des données');
      } finally { setIsLoading(false); }
    })();
  }, []);

  // R34dd : mémoriser le deck sélectionné
  useEffect(() => {
    if (!selectedDeck) return;
    try { localStorage.setItem('elementum_preferred_deck', selectedDeck); } catch (e) { /* ok */ }
  }, [selectedDeck]);

  useEffect(() => {
    if (tab !== 'custom') return undefined;
    const t = setInterval(refreshGames, 10000);
    return () => clearInterval(t);
  }, [tab, refreshGames]);

  // Duel tutoriel (mode IA facile) — lancement automatique
  useEffect(() => {
    if (!tutorialMode || tutorialStartedRef.current) return;
    if (isLoading || decks.length === 0) return;
    tutorialStartedRef.current = true;
    (async () => {
      try {
        const game = await createNewGame(preferredDeckId() || decks[0].id, { isAI: true, difficulty: 'easy' });
        if (game && game.gameId) { navigate('/game/battle/' + game.gameId + '?tutorial=1'); return; }
        throw new Error('creation impossible');
      } catch (err) {
        setError('Impossible de lancer le duel tutoriel - verifie ton deck puis reessaie');
      }
    })();
  }, [tutorialMode, isLoading, decks, navigate, createNewGame]);

  const handleReplayTutorial = () => {
    resetTutorial();
    window.dispatchEvent(new Event('elementum:tutorial-start'));
  };

  const stopQueuePolling = () => {
    if (queuePollRef.current) { clearInterval(queuePollRef.current); queuePollRef.current = null; }
  };

  // R34g : proposition de duel — fenêtre d'acceptation mutuelle.
  // On ne navigue PLUS directement : le duel ne démarre que si les 2
  // joueurs acceptent (sinon annulation + remboursement des mises).
  const [matchOffer, setMatchOffer] = useState(null);
  const [offerCountdown, setOfferCountdown] = useState(15);
  const [offerAnswered, setOfferAnswered] = useState(false);

  React.useEffect(() => {
    socketService.onMatchFound((data) => {
      if (data && data.gameId) {
        stopQueuePolling();
        setRankedSearching(false);
        setOfferAnswered(false);
        setMatchOffer({
          gameId: data.gameId,
          opponent: data.opponent || 'Adversaire',
          stake: data.stake || 0,
          pot: data.pot || 0,
          ranked: data.ranked,
          deadline: data.deadline || Date.now() + 15000,
        });
      }
    });
    const offConfirmed = socketService.onMatchConfirmed
      ? socketService.onMatchConfirmed((data) => {
          if (!data || !data.gameId) return;
          setMatchOffer((offer) => {
            if (offer && offer.gameId === data.gameId) {
              socketService.joinGame(data.gameId);
              navigate(`/game/battle/${data.gameId}`);
              return null;
            }
            return offer;
          });
        })
      : null;
    const offCancelled = socketService.onMatchCancelled
      ? socketService.onMatchCancelled((data) => {
          setMatchOffer((offer) => {
            if (offer && (!data.gameId || offer.gameId === data.gameId)) {
              setOfferAnswered(false);
              setError((data.reason) || 'Duel annulé — adversaire indisponible');
              return null;
            }
            return offer;
          });
        })
      : null;
    return () => {
      stopQueuePolling();
      if (offConfirmed) offConfirmed();
      if (offCancelled) offCancelled();
    };
  }, [navigate]);

  // Compte à rebours de la proposition de duel (1 tick/s)
  React.useEffect(() => {
    if (!matchOffer) return undefined;
    setOfferCountdown(Math.max(0, Math.ceil((matchOffer.deadline - Date.now()) / 1000)));
    const t = setInterval(() => {
      const left = Math.max(0, Math.ceil((matchOffer.deadline - Date.now()) / 1000));
      setOfferCountdown(left);
    }, 500);
    return () => clearInterval(t);
  }, [matchOffer]);

  const handleAcceptOffer = async () => {
    if (!matchOffer || offerAnswered) return;
    setOfferAnswered(true);
    try {
      const r = await confirmMatch(matchOffer.gameId);
      if (r && r.confirmed) {
        socketService.joinGame(matchOffer.gameId);
        navigate(`/game/battle/${matchOffer.gameId}`);
        setMatchOffer(null);
      }
      // sinon : on attend l'acceptation de l'adversaire (match-confirmed)
    } catch (e) {
      setOfferAnswered(false);
      setMatchOffer(null);
      setError('Confirmation impossible — duel annulé');
    }
  };

  const handleDeclineOffer = async () => {
    if (!matchOffer) return;
    const gid = matchOffer.gameId;
    setMatchOffer(null);
    setOfferAnswered(false);
    try { await declineMatch(gid); } catch (e) { /* déjà annulé côté serveur */ }
    setError('Duel refusé');
  };

  // Salle d'attente : un adversaire rejoint la partie créée → on démarre
  React.useEffect(() => {
    if (!lobby) return undefined;
    const off = socketService.onPlayerJoined((data) => {
      // le créateur reçoit player-joined dès qu'un 2e joueur join-game
      navigate(`/game/battle/${lobby.gameId}`);
    });
    socketService.joinGame(lobby.gameId);
    return () => { off(); };
  }, [lobby, navigate]);

  const pollQueueStatus = async () => {
    try {
      const st = await getQueueStatus();
      if (st && st.gameId) {
        // R34g : repli HTTP — mêmes garanties, fenêtre d'acceptation
        stopQueuePolling();
        setRankedSearching(false);
        if (!matchOffer || matchOffer.gameId !== st.gameId) {
          setOfferAnswered(false);
          setMatchOffer((o) => (o && o.gameId === st.gameId ? o : {
            gameId: st.gameId,
            opponent: 'Adversaire',
            stake: st.stake || 0,
            pot: st.pot || 0,
            ranked: true,
            deadline: Date.now() + 15000,
          }));
        }
        return;
      }
      setQueueInfo(st);
      if (st && st.inQueue === false) { stopQueuePolling(); setRankedSearching(false); }
    } catch (e) { /* tolérant */ }
  };

  // PvP (classique ou classé) → toujours la file de matchmaking
  const handleFindOpponent = async () => {
    if (!selectedDeck) { setError('Sélectionne un deck pour jouer'); return; }
    try {
      setError(null);
      const st = await joinRankedQueue(selectedDeck, { mode: pvpMode, stake: Number(stake) || 0 });
      if (!st || st.success === false) throw new Error(st && st.message ? st.message : 'File indisponible');
      setRankedSearching(true);
      setQueueInfo(st);
      stopQueuePolling();
      queuePollRef.current = setInterval(pollQueueStatus, 2000);
    } catch (err) {
      setError("Impossible de rejoindre la file : " + (err.message || 'erreur'));
    }
  };

  const handleCancelSearch = async () => {
    stopQueuePolling();
    setRankedSearching(false);
    setQueueInfo(null);
    await leaveRankedQueue();
  };

  // IA → partie immédiate
  const handleStartAI = async () => {
    if (!selectedDeck) { setError('Sélectionne un deck pour jouer'); return; }
    try {
      setIsCreatingGame(true);
      const game = await createNewGame(selectedDeck, { isAI: true, difficulty: aiDifficulty });
      if (game && game.gameId) navigate(`/game/battle/${game.gameId}`);
      else throw new Error('échec');
    } catch (err) {
      setError('Erreur lors de la création de la partie');
    } finally { setIsCreatingGame(false); }
  };

  // Création de partie → salle d'attente (PLUS de mulligan immédiat)
  const handleCreateGame = async () => {
    if (!selectedDeck) { setError('Sélectionne un deck pour jouer'); return; }
    try {
      setIsCreatingGame(true);
      const game = await createNewGame(selectedDeck);
      if (game && game.gameId) {
        setLobby({ gameId: game.gameId });
      } else throw new Error('échec');
    } catch (err) {
      setError('Erreur lors de la création de la partie');
    } finally { setIsCreatingGame(false); }
  };

  const handleJoinGame = async (gameId) => {
    if (!selectedDeck) { setError('Sélectionne un deck pour jouer'); return; }
    try {
      setIsJoiningGame(true);
      await joinExistingGame(gameId, selectedDeck);
      socketService.joinGame(gameId);
      navigate(`/game/battle/${gameId}`);
    } catch (err) {
      setError('Erreur lors de la connexion à la partie');
    } finally { setIsJoiningGame(false); }
  };

  const getElementEmoji = (element) => {
    const elements = { fire: '🔥', water: '💧', earth: '🌿', light: '✨', dark: '🌑', air: '🌪️' };
    return elements[(element || '').toLowerCase()] || '🃏';
  };

  if (!isLoading && decks.length === 0) {
    return (
      <div className="matchmaking-container">
        <div className="no-decks-card">
          <h2>Aucun deck disponible</h2>
          <p>Crée au moins un deck pour commencer à jouer</p>
          <button className="create-deck-button" onClick={() => navigate('/decks/builder')}>
            <span className="create-deck-icon">+</span> Créer un deck
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="matchmaking-container mm-v2">
      <h1>Duel</h1>
      {error && <div className="matchmaking-error">{error}</div>}

      {/* Onglets */}
      <div className="mm-tabs">
        <button type="button" className={`mm-tab ${tab === 'pvp' ? 'active' : ''}`} onClick={() => setTab('pvp')}>
          <span className="mm-tab-icon">👥</span> Joueur vs Joueur
        </button>
        <button type="button" className={`mm-tab ${tab === 'ai' ? 'active' : ''}`} onClick={() => setTab('ai')}>
          <span className="mm-tab-icon">🤖</span> Contre l'IA
        </button>
        <button type="button" className={`mm-tab ${tab === 'custom' ? 'active' : ''}`} onClick={() => setTab('custom')}>
          <span className="mm-tab-icon">🛠️</span> Création de partie
        </button>
      </div>

      <div className="mm-body">
        {/* ── Deck (commun PvP + IA) ── */}
        {tab !== 'custom' && (
          <section className="mm-card mm-deck">
            <h2 className="mm-card-title">🃏 Ton deck</h2>
            {isLoading ? <p className="mm-hint">Chargement…</p> : (
              <>
                <select
                  className="mm-deck-select"
                  value={selectedDeck || ''}
                  onChange={(e) => setSelectedDeck(e.target.value)}
                >
                  {decks.map((deck) => (
                    <option key={deck.id} value={deck.id}>{deck.name} ({deck.element})</option>
                  ))}
                </select>
                {selectedDeck && decks.filter((d) => d.id === selectedDeck).map((deck) => (
                  <div key={deck.id} className="mm-deck-preview">
                    <span className={`mm-deck-el ${String(deck.element).toLowerCase()}`}>{getElementEmoji(deck.element)}</span>
                    <div>
                      <p className="mm-deck-name">{deck.name}</p>
                      <p className="mm-deck-count">{deck.cards.length} cartes</p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </section>
        )}

        {/* ── PvP ── */}
        {tab === 'pvp' && (
          <section className="mm-card mm-pvp">
            {!rankedSearching ? (
              <>
                <h2 className="mm-card-title">👥 Joueur vs Joueur</h2>
                <div className="mm-submodes">
                  <button type="button" className={`mm-submode mm-submode--casual ${pvpMode === 'casual' ? 'active' : ''}`} onClick={() => setPvpMode('casual')}>
                    <span className="mm-submode-name">🎲 Classique</span>
                    <span className="mm-submode-desc">Sans Elo — 10 pièces par victoire</span>
                  </button>
                  <button type="button" className={`mm-submode mm-submode--ranked ${pvpMode === 'ranked' ? 'active' : ''}`} onClick={() => setPvpMode('ranked')}>
                    <span className="mm-submode-name">🏆 Classé</span>
                    <span className="mm-submode-desc">Elo en jeu — jusqu'à ±20 points</span>
                  </button>
                </div>

                <div className="mm-stakes">
                    <p className="mm-stakes-label">💡 Mise (optionnelle)</p>
                    <div className="wager-tiers">
                      {STAKES.map((v) => (
                        <button key={v} type="button" className={`wager-tier ${stake === v ? 'active' : ''}`} onClick={() => setStake(v)}>
                          {v === 0 ? 'Gratuit' : v}
                        </button>
                      ))}
                    </div>
                    {stake > 0 && (
                      <p className="wager-hint">
                        Pot : <strong>{stake * 2}</strong> pièces — le vainqueur empoche le pot moins 5% de frais (brûlés), même en classique.
                        Email vérifié requis.
                      </p>
                    )}
                  </div>

                <button type="button" className="mm-cta" onClick={handleFindOpponent} disabled={isLoading || !selectedDeck}>
                  🔎 Trouver un adversaire
                </button>
                <p className="mm-hint">Même mode, même mise. Appariement par proximité d'Elo.</p>
              </>
            ) : (
              <div className="ranked-searching">
                <div className="ranked-searching-spinner" />
                <p className="ranked-searching-title">Recherche d'un adversaire…</p>
                <p className="ranked-searching-sub">
                  {queueInfo && queueInfo.playersInQueue > 1
                    ? `${queueInfo.playersInQueue} duellistes dans la file`
                    : "La file s'étoffe — reste dans les parages"}
                </p>
                <p className="ranked-searching-mode">
                  {pvpMode === 'ranked' ? '🏆 Classé' : '🎲 Classique'}
                  {queueInfo && queueInfo.stake > 0 ? ` · 💡 Mise ${queueInfo.stake}` : ''}
                </p>
                <button type="button" className="mm-cancel" onClick={handleCancelSearch}>Annuler la recherche</button>
              </div>
            )}
          </section>
        )}

        {/* ── IA ── */}
        {tab === 'ai' && (
          <section className="mm-card mm-ai">
            <h2 className="mm-card-title">🤖 Contre l'IA</h2>
            <div className="difficulty-buttons">
              {[['easy', 'Facile'], ['medium', 'Moyen'], ['hard', 'Difficile'], ['expert', 'Expert']].map(([v, label]) => (
                <button key={v} type="button" className={`difficulty-button ${aiDifficulty === v ? 'active' : ''}`} onClick={() => setAiDifficulty(v)}>
                  {label}
                </button>
              ))}
            </div>
            <p className="mm-hint">Entraînement sans enjeu — aucun Elo, aucune pièce.</p>
            <button type="button" className="mm-cta" onClick={handleStartAI} disabled={isCreatingGame || isLoading || !selectedDeck}>
              {isCreatingGame ? 'Création…' : '🎮 Commencer le duel'}
            </button>
          </section>
        )}

        {/* ── Création de partie ── */}
        {tab === 'custom' && (
          <section className="mm-card mm-custom">
            {!lobby ? (
              <>
                <h2 className="mm-card-title">🛠️ Création de partie</h2>
                <p className="mm-hint">Crée une partie ouverte : elle apparaît dans la liste ci-dessous, tu pars en duel dès qu'un joueur la rejoint.</p>
                <div className="mm-deck-inline">
                  <select className="mm-deck-select" value={selectedDeck || ''} onChange={(e) => setSelectedDeck(e.target.value)}>
                    {decks.map((deck) => (
                      <option key={deck.id} value={deck.id}>{deck.name} ({deck.element})</option>
                    ))}
                  </select>
                </div>
                <button type="button" className="mm-cta" onClick={handleCreateGame} disabled={isCreatingGame || isLoading || !selectedDeck}>
                  {isCreatingGame ? 'Création…' : '➕ Créer la partie'}
                </button>
              </>
            ) : (
              <div className="mm-lobby">
                <div className="ranked-searching-spinner" />
                <p className="ranked-searching-title">Partie créée — en attente d'un adversaire…</p>
                <p className="mm-lobby-code">Code : <strong>{String(lobby.gameId).slice(-6).toUpperCase()}</strong></p>
                <p className="mm-hint">Le duel démarre automatiquement dès qu'un joueur rejoint. Communique ce code pour jouer avec un ami.</p>
              </div>
            )}

            <div className="mm-open-games">
              <h3 className="mm-card-title">🎲 Parties ouvertes</h3>
              {availableGames.length === 0 ? (
                <p className="mm-hint">Aucune partie ouverte pour le moment.</p>
              ) : (
                <div className="games-list">
                  {availableGames.map((game) => (
                    <div key={game.id} className="game-item">
                      <div className="game-info">
                        <div className="game-host">
                          <span className="host-name">{game.host.username}</span>
                          <span className="host-rank">{game.host.rank}</span>
                        </div>
                        <div className="game-details">
                          <span className="game-mode-chip">{getElementEmoji(game.element)} {game.element}</span>
                          <span className="game-created">⏱️ {timeSince(new Date(game.createdAt))}</span>
                        </div>
                      </div>
                      <button className="join-button" onClick={() => handleJoinGame(game.id)} disabled={isJoiningGame}>
                        {isJoiningGame ? 'Connexion…' : '➡️ Rejoindre'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {matchOffer && (
        <div className="mm-offer-overlay" role="dialog" aria-modal="true">
          <div className="mm-offer">
            <div className="mm-offer-title">⚔️ Duel trouvé !</div>
            <div className="mm-offer-opponent">
              vs <strong>{matchOffer.opponent}</strong>
              {matchOffer.ranked === false ? ' 🎲 Classique' : ' 🏆 Classé'}
            </div>
            {matchOffer.stake > 0 ? (
              <div className="mm-offer-stake">
                Mise : <strong>{matchOffer.stake} 🪙</strong> chacun — Pot : <strong>{matchOffer.pot} 🪙</strong>
              </div>
            ) : (
              <div className="mm-offer-stake">Aucune mise en jeu</div>
            )}
            <div className={'mm-offer-countdown' + (offerCountdown <= 5 ? ' urgent' : '')}>
              {offerAnswered ? "En attente de l'adversaire…" : <>Acceptes-tu le duel ? ({offerCountdown}s)</>}
            </div>
            <div className="mm-offer-actions">
              <button
                type="button"
                className="mm-offer-btn accept"
                onClick={handleAcceptOffer}
                disabled={offerAnswered}
              >
                {offerAnswered ? '⏳ En attente…' : '⚔️ Accepter'}
              </button>
              <button
                type="button"
                className="mm-offer-btn decline"
                onClick={handleDeclineOffer}
                disabled={offerAnswered}
              >
                ✖ Refuser
              </button>
            </div>
          </div>
        </div>
      )}

      <button type="button" className="game-mode-button tutorial-replay-button mm-replay" onClick={handleReplayTutorial}>
        <span className="game-mode-icon">🎓</span>
        <span className="game-mode-name">Rejouer le tutoriel</span>
        <span className="game-mode-desc">Visite guidée + duel tutoriel</span>
      </button>
    </div>
  );
};

const timeSince = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = Math.floor(seconds / 3600);
  if (interval >= 1) return interval + 'h';
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return interval + 'm';
  return '< 1m';
};

export default Matchmaking;
